import { Button, CloseButton, Dialog, Portal } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { t } from 'i18next';
import { useState } from 'react';

import { ApiError, CommitmentsService } from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { handleError } from '@/utils';

interface DeleteCommitmentModalProps {
  commitmentId: string;
  commitmentName: string;
  onDelete?: (id: string) => void;
  onCancel?: () => void;
  children: React.ReactNode;
}

export const CommitmentDeleteDialog = ({
  commitmentId,
  commitmentName,
  onDelete,
  onCancel,
  children,
}: DeleteCommitmentModalProps) => {
  const { slug } = useParams({ strict: false });
  const queryClient = useQueryClient();
  const showToast = useCustomToast();
  const [open, setOpen] = useState(false);

  const mutationCommitment = useMutation({
    mutationFn: async () =>
      await CommitmentsService.deleteCommitment({
        companySlug: slug,
        id: commitmentId,
      }),
    onSuccess: async () => {
      showToast('Succès', 'Engagement supprimer avec succès', 'success');
      await queryClient.invalidateQueries({ queryKey: ['csrPlans'] });
      setOpen(false);
      onDelete && onDelete(commitmentId);
    },
    onError: (err: ApiError) => {
      handleError(err, showToast);
    },
  });

  const handleDelete = () => {
    mutationCommitment.mutate();
  };

  return (
    <Dialog.Root role="alertdialog" lazyMount open={open} onOpenChange={(e) => setOpen(e.open)}>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>

      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Supprimer "{commitmentName}" ?</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <p>
                Cette action est irréversible. L'engagement <strong>{commitmentName}</strong> sera définitivement
                supprimé du plan RSE, ainsi que toutes ses données associées.
              </p>
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild onClick={() => onCancel && onCancel()}>
                <Button variant="outline">{t('ui.cancel')}</Button>
              </Dialog.ActionTrigger>

              <Button bg="red.500" onClick={handleDelete}>
                {t('ui.delete')}
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
